<template>
  <v-container :key="user.id" fluid>
    <ChangePasswordDialog
      v-if="showChangePasswordDialog"
      data-cc="profile-page-change-password-dialog"
      @closeDialog="showChangePasswordDialog = false"
    />

    <NewUserDialog
      v-if="showUserData.dialog"
      :showUserData="showUserData"
      data-cc="profile-page-user-dialog"
      @close="toggleUserDataDialog()"
      @userUpdated="$emit('userUpdated')"
    />

    <v-row>
      <v-col cols="12" class="text-h5">
        {{ user.firstName }} {{ user.lastName }}
      </v-col>
    </v-row>

    <v-row>
      <v-col v-if="user.firstName" cols="12" md="6">
        <v-text-field
          :label="$t('generic.firstname')"
          :modelValue="user.firstName"
          readOnly
          density="comfortable"
          data-cc="profile-page-first-name"
        />
      </v-col>

      <v-col v-if="user.lastName" cols="12" md="6">
        <v-text-field
          :label="$t('generic.lastname')"
          :modelValue="user.lastName"
          readOnly
          density="comfortable"
          data-cc="profile-page-last-name"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col v-if="user.email" cols="12" md="6">
        <v-text-field
          :label="$t('generic.email')"
          :modelValue="user.email"
          readOnly
          density="comfortable"
          data-cc="profile-page-email"
        />
      </v-col>

      <v-col v-if="user.mobilePhone" cols="12" md="6">
        <v-text-field
          :label="$t('generic.mobileNumber')"
          :modelValue="user.mobilePhone"
          readOnly
          data-cc="profile-page-mobile-number"
        />
      </v-col>

      <v-col v-if="user.landLine" cols="12" md="6">
        <v-text-field
          :label="$t('generic.landline')"
          :modelValue="user.landLine"
          readOnly
          data-cc="profile-landline"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          :label="$t('userProfile.created')"
          :modelValue="getUserFormattedDateTime(user.created)"
          readOnly
          density="comfortable"
          data-cc="profile-page-created"
        />
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          :label="$t('userProfile.lastLogin')"
          :modelValue="
            user.hasOwnProperty('lastLogin')
              ? getUserFormattedDateTime(user.lastLogin)
              : $t('userProfile.notLoggedIn')
          "
          density="comfortable"
          readOnly
          data-cc="profile-page-last-login"
        />
      </v-col>
    </v-row>

    <v-row v-if="isOwnProfilePage || features?.updateBOUsers">
      <v-col cols="12">
        <BMButton
          v-if="isOwnProfilePage"
          :title="$t('userProfile.changePassword')"
          data-cc="profile-page-change-password-button"
          @clicked="showChangePasswordDialog = true"
        />

        <BMButton
          v-else-if="features?.updateBOUsers"
          :title="$t('generic.edit')"
          data-cc="profile-page-edit-button"
          @clicked="toggleUserDataDialog(true)"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';
import dayjs from 'dayjs';

import BMButton from '@/components/common/utilities/BMButton.vue';
import ChangePasswordDialog from '@/components/account/ChangePasswordDialog.vue';
import NewUserDialog from '@/components/common/user/NewUserDialog.vue';
import { usePermissions } from '@/composables/usePermissions';

import { type User } from '@/types/api/user';

interface ShowUserData {
  dialog: boolean;
  user: User;
  type: string | null;
}

const props = defineProps<{
  user: User;
}>();

defineEmits<{
  (e: 'userUpdated'): void;
}>();

const route = useRoute();
const showChangePasswordDialog = ref(false);

const showUserData = ref<ShowUserData>({
  dialog: false,
  user: {} as User,
  type: null
});

const isOwnProfilePage = computed(() => {
  return !Object.prototype.hasOwnProperty.call(route.params, 'userHashId');
});

// Global properties
const instance = getCurrentInstance();
const { $userSettings } = instance?.appContext.config.globalProperties || {};

// Permissions
const { features } = usePermissions();

const getUserFormattedDateTime = (timestamp: string | null | undefined) => {
  if (!timestamp) return '-';

  const datePart = dayjs(timestamp).format($userSettings.dateMediumFormat);
  const timePart = dayjs(timestamp).format($userSettings.timeLongFormat);

  return `${datePart} ${timePart}`;
};

const toggleUserDataDialog = (open = false) => {
  showUserData.value = {
    dialog: open,
    user: open ? props.user : ({} as User),
    type: 'edit'
  };
};
</script>
